// ─── Plans (multiples) — modèle propriétaire/accès ────────────────
// Un PLAN = un lieu physique (Maison, Garage, Cave…) avec son propre arbre
// d'emplacements. Collection `plans/{id}` PARALLÈLE à `inventaires` :
//   { espaceId, nom, emoji, arbre:[noeud], acces:[uid], lecteurs, redacteurs,
//     creePar, creeParNom, creeLe }
// Emplacements PARTAGÉS : n'importe quel article de n'importe quel inventaire de
// l'espace peut être rangé dans un emplacement de n'importe quel plan (l'article
// référence l'id d'emplacement ; l'id est unique par nœud).
// `acces` = dénormalisé (créateur ∪ propriétaire d'espace ∪ lecteurs ∪ rédacteurs)
// → requête `where acces array-contains uid` (liste par accès, tous espaces).
import { db } from '@mop/core';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, query, where,
} from 'firebase/firestore';
import { calcAcces, listesDepuisAccesN } from './inventaires';
import { rangNiveau, accesDepuisListes } from '@mop/core';

// Tous les plans où j'ai un accès, à travers TOUS les espaces (une seule requête).
export function ecouterPlansAccessibles(uid, callback, onErreur) {
  if (!uid) { callback([]); return () => {}; }
  return onSnapshot(
    query(collection(db, 'plans'), where('acces', 'array-contains', uid)),
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a, b) => String(a.creeLe || '').localeCompare(String(b.creeLe || '')));
      callback(l);
    },
    (e) => { callback([]); onErreur?.(e); },
  );
}

// Plans d'un ESPACE (section « Qui voit quoi » du Profil : un plan = une
// ressource, comme un inventaire).
export function ecouterPlansEspace(espaceId, callback, onErreur) {
  if (!espaceId) { callback([]); return () => {}; }
  return onSnapshot(
    query(collection(db, 'plans'), where('espaceId', '==', espaceId)),
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a, b) => String(a.creeLe || '').localeCompare(String(b.creeLe || '')));
      callback(l);
    },
    (e) => { callback([]); onErreur?.(e); },
  );
}

// ── ACCÈS UNIFIÉ d'un plan (grammaire 3 niveaux du Core) ──
// `accesN` porte les niveaux ; `acces`/`lecteurs`/`redacteurs` restent écrits
// (miroirs des règles Firestore et de rolePlan).
export function accesPlan(plan) {
  if (plan?.accesN && typeof plan.accesN === 'object' && !Array.isArray(plan.accesN)) return plan.accesN;
  return accesDepuisListes(plan?.lecteurs, plan?.redacteurs);
}
export async function definirAccesPlanN(planId, { creePar, ownerUid, membresUids, acces }) {
  const a = acces || {};
  const { lecteurs, redacteurs } = listesDepuisAccesN(a);
  await updateDoc(doc(db, 'plans', planId), {
    accesN:    a,
    acces:     calcAcces({ creePar, ownerUid, accesN: a, membresUids }),
    accesTous: rangNiveau(a['*']) >= 1,
    lecteurs, redacteurs,
  });
}

export async function creerPlan({ espaceId, nom, emoji, arbre, creePar, creeParNom, ownerUid }) {
  if (!espaceId) throw new Error('Aucun espace');
  const ref = await addDoc(collection(db, 'plans'), {
    espaceId,
    // Canon `nom` SEUL (cf. core/elements).
    nom: (nom || '').trim() || 'Plan',
    emoji: emoji || '🗺️',
    arbre: Array.isArray(arbre) ? arbre : [],
    lecteurs: [], redacteurs: [],
    acces: calcAcces({ creePar, ownerUid }),
    creePar: creePar || null,
    creeParNom: creeParNom || '',
    creeLe: new Date().toISOString(),
  });
  return ref.id;
}

export async function renommerPlan(planId, nom) {
  await updateDoc(doc(db, 'plans', planId), { nom: (nom || '').trim() || 'Plan' });
}
export async function modifierEmojiPlan(planId, emoji) {
  await updateDoc(doc(db, 'plans', planId), { emoji: emoji || '🗺️' });
}
export async function modifierArbrePlan(planId, arbre) {
  await updateDoc(doc(db, 'plans', planId), { arbre: Array.isArray(arbre) ? arbre : [] });
}
export async function definirPartagePlan(planId, { creePar, ownerUid, lecteurs, redacteurs }) {
  const lec = (Array.isArray(lecteurs) ? lecteurs : []).filter(Boolean);
  const red = (Array.isArray(redacteurs) ? redacteurs : []).filter(Boolean);
  await updateDoc(doc(db, 'plans', planId), {
    lecteurs: lec, redacteurs: red,
    acces: calcAcces({ creePar, ownerUid, lecteurs: lec, redacteurs: red }),
  });
}
export async function supprimerPlan(planId) {
  await deleteDoc(doc(db, 'plans', planId));
}

// Rôle d'un utilisateur SUR un plan (mêmes règles que les inventaires).
export function rolePlan(plan, uid, ownerUid) {
  if (!uid) return 'aucun';
  if (uid === ownerUid || uid === plan?.creePar) return 'proprietaire';
  if ((plan?.redacteurs || []).includes(uid)) return 'redacteur';
  if (Array.isArray(plan?.acces) && plan.acces.includes(uid)) return 'lecteur';
  return 'aucun';
}
